<template>
  <v-container fluid class="pa-4 bg-grey-lighten-4">
    <v-row v-if="showWhatsappInbox && whatsappInboxForInstitution && whatsappInboxConnected" class="mb-2">
      <v-col cols="12" md="auto">
        <whatsapp-inbox-button
          :institution-id="effectiveInstitutionId"
          @error="showSnackbar"
        />
      </v-col>
    </v-row>

    <v-row v-if="isStaff" class="mb-4">
      <v-col cols="12" md="6">
        <v-autocomplete
          v-model="staffInstitutionId"
          :items="institutions"
          :label="$t('settings.whatsapp.selectInstitution')"
          :hint="$t('settings.whatsapp.selectInstitutionHint')"
          :loading="loadingInstitutions"
          item-title="title"
          item-value="_id"
          variant="outlined"
          persistent-hint
          color="red-darken-3"
          @update:model-value="onStaffInstitutionChange"
        />
      </v-col>
    </v-row>

    <v-row v-if="loading" justify="center" class="my-8">
      <v-progress-circular indeterminate color="red-darken-3" size="64" />
    </v-row>

    <template v-else>
      <v-alert
        v-if="loadError"
        type="error"
        variant="tonal"
        class="mb-4"
      >
        {{ loadError }}
      </v-alert>

      <v-alert
        v-if="devMockActive"
        type="info"
        variant="tonal"
        density="compact"
        class="mb-4"
      >
        {{ $t('settings.whatsapp.devMock', { state: uiState }) }}
      </v-alert>

      <whatsapp-not-connected
        v-if="!loadError && uiState === 'not_connected' && effectiveInstitutionId"
        :institution-id="effectiveInstitutionId"
        :institution-name="effectiveInstitutionName"
        :is-staff="isStaff"
        @connected="reloadWhatsapp"
        @snackbar="showSnackbar"
      />

      <whatsapp-syncing
        v-else-if="!loadError && uiState === 'syncing' && whatsapp"
        :whatsapp="whatsapp"
        :institution-id="effectiveInstitutionId"
        :is-staff="isStaff"
        @snackbar="showSnackbar"
        @reload="reloadWhatsapp"
      />

      <whatsapp-connected
        v-else-if="!loadError && uiState === 'connected' && whatsapp && effectiveInstitutionId"
        :whatsapp="whatsapp"
        :institution-id="effectiveInstitutionId"
        :is-staff="isStaff"
        @snackbar="showSnackbar"
        @reload="reloadWhatsapp"
      />

      <v-card
        v-else-if="!loadError && uiState === 'disconnected'"
        rounded="lg"
        variant="outlined"
        class="pa-4 text-medium-emphasis"
      >
        <p>{{ $t('settings.whatsapp.placeholderBody') }}</p>
        <p class="text-caption mt-2">
          {{ $t(`settings.whatsapp.states.${uiState}`) }}
        </p>
      </v-card>
    </template>

    <v-snackbar
      v-model="snackbar.value"
      :color="snackbar.color"
      location="top"
      :timeout="6000"
    >
      {{ snackbar.message }}
    </v-snackbar>
  </v-container>
</template>

<script>
import getRequest from '@/api/getRequest';
import { getWhatsapp } from '@/api/whatsapp';
import getWhatsappUiState from '@/utils/whatsappState';
import { whatsappErrorMessage } from '@/utils/whatsappErrors';
import WhatsappNotConnected from '@/components/Settings/WhatsApp/WhatsappNotConnected.vue';
import WhatsappSyncing from '@/components/Settings/WhatsApp/WhatsappSyncing.vue';
import WhatsappConnected from '@/components/Settings/WhatsApp/WhatsappConnected.vue';
import WhatsappInboxButton from '@/components/WhatsApp/WhatsappInboxButton.vue';
import { hasPortalPermission, PORTAL_PERMISSIONS } from '@/utils/portalPermissions';

const STAFF_ROLES = ['SuperAdmin'];
const POLL_MS = 10000;

function mockWhatsapp(state) {
  const base = {
    settings: {
      answer_first: 'staff',
      wait_minutes: 3,
      pause_after_staff_hours: 4,
      opening_hours: [],
    },
    private_contacts: [],
  };
  switch (state) {
    case 'syncing':
      return {
        ...base,
        mode: 'coexistence',
        status: 'syncing',
        display_phone_number: '+39 333 000 0000',
        verified_name: 'Mock Yard',
        sync_progress: 40,
      };
    case 'active':
      return {
        ...base,
        mode: 'coexistence',
        status: 'active',
        display_phone_number: '+39 333 000 0000',
        verified_name: 'Mock Yard',
        quality_rating: 'GREEN',
        connected_at: new Date().toISOString(),
      };
    case 'disconnected':
      return {
        ...base,
        mode: 'coexistence',
        status: 'disconnected',
        display_phone_number: '+39 333 000 0000',
        verified_name: 'Mock Yard',
        disconnect_reason: 'PRIMARY_INACTIVITY',
        disconnected_at: new Date().toISOString(),
      };
    case 'none':
    default:
      return {
        ...base,
        mode: 'none',
        status: 'not_connected',
      };
  }
}

export default {
  name: 'WhatsappSettings',
  components: {
    'whatsapp-not-connected': WhatsappNotConnected,
    'whatsapp-syncing': WhatsappSyncing,
    'whatsapp-connected': WhatsappConnected,
    WhatsappInboxButton,
  },
  props: {
    institution: {
      type: Object,
      default: null,
    },
  },
  data() {
    return {
      whatsapp: null,
      loading: true,
      loadError: null,
      institutions: [],
      loadingInstitutions: false,
      staffInstitutionId: null,
      pollTimer: null,
      snackbar: {
        value: false,
        message: '',
        color: 'info',
      },
    };
  },
  computed: {
    isStaff() {
      return STAFF_ROLES.includes(this.$store.getters.role);
    },
    showWhatsappInbox() {
      return hasPortalPermission(this.$store, PORTAL_PERMISSIONS.WHATSAPP_INBOX);
    },
    whatsappInboxForInstitution() {
      return Boolean(this.effectiveInstitutionId);
    },
    whatsappInboxConnected() {
      const wa = this.whatsapp;
      if (!wa || !wa.phone_number_id) {
        return false;
      }
      if (wa.status === 'not_connected' || wa.status === 'disconnected' || wa.status === 'disabled') {
        return false;
      }
      return this.uiState === 'connected' || wa.status === 'active';
    },
    effectiveInstitutionId() {
      if (this.isStaff) {
        const fromQuery = this.$route.query.institutionId;
        if (fromQuery && typeof fromQuery === 'string') {
          return fromQuery;
        }
        return this.$store.getters.institutionId;
      }
      return this.$store.getters.institutionId;
    },
    devMockActive() {
      if (process.env.NODE_ENV !== 'development') {
        return false;
      }
      const mock = this.$route.query.waMock;
      return typeof mock === 'string' && mock.length > 0;
    },
    uiState() {
      return getWhatsappUiState(this.whatsapp);
    },
    effectiveInstitutionName() {
      const id = this.effectiveInstitutionId;
      if (!id) {
        return '';
      }
      const match = this.institutions.find(
        (item) => String(item._id) === String(id),
      );
      if (match && match.title) {
        return match.title;
      }
      if (this.institution && String(this.institution._id) === String(id)) {
        return this.institution.title || '';
      }
      return '';
    },
  },
  watch: {
    effectiveInstitutionId: {
      immediate: false,
      handler() {
        this.reloadWhatsapp();
      },
    },
    uiState(newState) {
      if (newState === 'syncing') {
        this.startPolling();
      } else {
        this.stopPolling();
      }
    },
    '$route.query.waMock'() {
      this.reloadWhatsapp();
    },
  },
  mounted() {
    if (this.isStaff) {
      this.staffInstitutionId = this.effectiveInstitutionId;
      this.loadInstitutions();
    } else if (this.institution && this.institution.title) {
      this.institutions = [{ _id: this.institution._id, title: this.institution.title }];
    }
    this.reloadWhatsapp();
  },
  beforeUnmount() {
    this.stopPolling();
  },
  methods: {
    loadInstitutions() {
      this.loadingInstitutions = true;
      getRequest('/institution/', '')
        .then((response) => {
          this.institutions = response.institutions || [];
        })
        .catch(() => {
          this.institutions = [];
        })
        .finally(() => {
          this.loadingInstitutions = false;
        });
    },
    onStaffInstitutionChange(id) {
      if (!id) {
        return;
      }
      this.$router.replace({
        query: {
          ...this.$route.query,
          institutionId: id,
        },
      });
    },
    async reloadWhatsapp() {
      this.loading = true;
      this.loadError = null;
      try {
        if (this.devMockActive) {
          this.whatsapp = mockWhatsapp(this.$route.query.waMock);
          return;
        }
        const id = this.effectiveInstitutionId;
        if (!id) {
          this.loadError = this.$t('settings.whatsapp.noInstitution');
          this.whatsapp = null;
          return;
        }
        const response = await getWhatsapp(id);
        this.whatsapp = response.whatsapp || null;
      } catch (err) {
        this.loadError = whatsappErrorMessage(err);
        this.whatsapp = null;
      } finally {
        this.loading = false;
        if (this.uiState === 'syncing' && !this.devMockActive) {
          this.startPolling();
        }
      }
    },
    startPolling() {
      if (this.devMockActive || this.pollTimer) {
        return;
      }
      this.pollTimer = setInterval(() => {
        this.pollWhatsapp();
      }, POLL_MS);
    },
    stopPolling() {
      if (this.pollTimer) {
        clearInterval(this.pollTimer);
        this.pollTimer = null;
      }
    },
    showSnackbar({ message, color }) {
      this.snackbar = {
        value: true,
        message: message || '',
        color: color || 'info',
      };
    },
    async pollWhatsapp() {
      if (this.devMockActive || this.loading) {
        return;
      }
      const id = this.effectiveInstitutionId;
      if (!id) {
        return;
      }
      try {
        const response = await getWhatsapp(id);
        this.whatsapp = response.whatsapp || null;
      } catch (err) {
        this.loadError = whatsappErrorMessage(err);
        this.stopPolling();
      }
    },
  },
};
</script>
